// @ts-nocheck
import logging from '@sstdev/lib_logging';
import { ErrorBoundary, hooks, Modal, Popper, testProperties } from 'lib_ui-primitives';
import { constants, dbViews, globalConfig, metadata } from 'lib_ui-services';
import lodash from 'lodash';
import PropTypes from 'prop-types';
import { memo, createElement as rc, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import useEventSink from '../../../hooks/useEventSink';
import useReads from '../../../hooks/useReads';
import useRegisterWithFilterInterdependencyBoundary from '../../../hooks/useRegisterWithFilterInterdependencyBoundary';
import { useFilterInterdependencyBoundary } from '../../contextProviders/FilterInterdependencyBoundary';
import { useMoveFocusToNext } from '../../contextProviders/FocusProvider';
import ReadProvider from '../../contextProviders/ReadProvider';
import ComboButton from './ComboButton';
import DownshiftProvider, { useDownshift } from './DownshiftProvider';
import DropDownInput from './DropDownInput';
import DropDownList from './DropDownList';
import { ComboContainer, InputAndButton, ScanLabelAndField } from './styles';
import useAssembleRecords from './useAssembleRecords';
import useInterceptRef from './useInterceptRef';
import useIsDisabledByFilter from './useIsDisabledByFilter';
const { get } = lodash;
const { set } = lodash;
const { uniq } = lodash;

const { getTitleAlternative } = metadata;
const { EMPTY_ARRAY } = dbViews.emptyDbView;
const { LOCAL_ONLY_SEARCH_REASONS } = dbViews;
const { minFullTextSearchTermLength } = globalConfig();

// #2047: what a dropdown says when its options are only the ones stored on this device. A grid
// says the same thing under its last row; a dropdown has no last row to speak of, so it says it in
// the list itself. Only `termTooShort` can offer a way out - the other two mean no term of any
// length will reach the server, so neither invites the user to keep typing.
const LOCAL_ONLY_NOTICE_TEXT = {
    [LOCAL_ONLY_SEARCH_REASONS.termTooShort]: `Type ${minFullTextSearchTermLength} or more characters to search all records`,
    [LOCAL_ONLY_SEARCH_REASONS.serverUnavailable]: 'Server search unavailable - showing options from this device',
    [LOCAL_ONLY_SEARCH_REASONS.noServerSearch]: 'Showing only options stored on this device'
};
const { useTimeout, useDebounce } = hooks;
const READS_RECEIVED_DEBOUNCE = 1000; // ms

// constant to avoid unnecessary rerenders
const emptyArray = [];
const EMPTY_HNODE = {};
const _p = {
    useAssembleRecords,
    useReads,
    selectItemTimeout: 1000,
    onStateChange,
    createPopper: Popper.createPopper,
    useMinimalItem: useMinimalEntry,
    hasOnlyOneRecord,
    useIsDisabledByFilter
};
export const _private = _p;
DropDown.propTypes = {
    hNode: PropTypes.shape({
        treePosition: PropTypes.shape({
            sequence: PropTypes.number.isRequired
        }).isRequired,
        required: PropTypes.bool
    }).isRequired,
    active: PropTypes.bool.isRequired
};
/**
 * There's a lot going on in here.
 * Some high level info:
 * Most of the logic for when the dropdown menu/list of items will show (and similar
 * timing and action handling logic) is handled by the downshift.js library.
 * Downshift will also try to add accessibility information.  Downshift expects us to
 * provide the actual UI elements that will receive the actions, updates, etc.
 * The popper.js library is for non-mobile only, and will handle laying out where the menu
 * of items is on the screen.  It tries to keep the menu on the screen, moving it around
 * as necessary (within reason).
 * Because of limited real-estate, the mobile view will display a modal for the menu of
 * items, trying to fill the whole screen.
 */
function DropDown(props) {
    // Some consumers of this abstract component use namespace/relation instead of
    // foreignNamespace/foreignRelation.
    const foreignNamespace = props?.hNode?.foreignNamespace || props?.hNode?.namespace;
    const foreignRelation = props?.hNode?.foreignRelation || props?.hNode?.relation;
    //this value will appear in the input itself (not the option list)  when empty
    const emptyValue = useMinimalEntry(props?.hNode, '', props?.hNode?.alwaysDisplayUnassignedRow);
    const {
        value: selectedItem = emptyValue,
        setValue,
        removeAllSelected,
        errors,
        hNode,
        autoFocus = false,
        active,
        selectedValues = emptyArray,
        setSelectedValues,
        hNode: {
            id,
            title,
            propertyName = getTitleAlternative(foreignNamespace, foreignRelation),
            inputDisplayProperties = emptyArray,
            dropdownDisplayProperties = emptyArray,
            alwaysDisplayUnassignedRow = false,
            hNodeType
        }
    } = props || { hNode: EMPTY_HNODE };

    // Some predefined (non-selectable) list entries
    const unassignedValue = useMinimalEntry(hNode, 'Unassigned', alwaysDisplayUnassignedRow);
    const loadingValue = useMinimalEntry(hNode, 'Loading...', alwaysDisplayUnassignedRow);
    const searchingValue = useMinimalEntry(hNode, 'Searching...', alwaysDisplayUnassignedRow);
    //this value will appear in the option list when there are no records
    const emptyListValue = useMinimalEntry(props?.hNode, props?.hNode?.emptyHint || '', alwaysDisplayUnassignedRow);
    // One entry per reason rather than one built from the current reason: these are compared by
    // identity in `onChange` below, and an entry rebuilt as the reason changes would not match the
    // row the user actually clicked.
    const termTooShortNotice = useLocalOnlyNotice(hNode, LOCAL_ONLY_SEARCH_REASONS.termTooShort);
    const serverUnavailableNotice = useLocalOnlyNotice(hNode, LOCAL_ONLY_SEARCH_REASONS.serverUnavailable);
    const noServerSearchNotice = useLocalOnlyNotice(hNode, LOCAL_ONLY_SEARCH_REASONS.noServerSearch);
    const localOnlyNoticeValues = useMemo(
        () => ({
            [LOCAL_ONLY_SEARCH_REASONS.termTooShort]: termTooShortNotice,
            [LOCAL_ONLY_SEARCH_REASONS.serverUnavailable]: serverUnavailableNotice,
            [LOCAL_ONLY_SEARCH_REASONS.noServerSearch]: noServerSearchNotice
        }),
        [termTooShortNotice, serverUnavailableNotice, noServerSearchNotice]
    );

    const moveFocusToNext = useMoveFocusToNext();

    // The metadata defines how the input text should be formatted
    const inputItemToString = useCallback(
        item => getItemValuesText(item, propertyName, inputDisplayProperties),
        [propertyName, inputDisplayProperties]
    );

    // The metadata defines how the list entry text should be formatted
    const listItemToString = useCallback(
        item => getItemValuesText(item, propertyName, dropdownDisplayProperties),
        [propertyName, dropdownDisplayProperties]
    );

    const { native } = useTheme();

    const [inputValue, setInputValue] = useState(inputItemToString(selectedItem));
    const [subscribe] = useEventSink();

    // Tracks that a cascade clear is in flight so onStateChange/addInputChangeHandler
    // don't restore the old building name while the form machine oscillates.
    const isCascadeClearingRef = useRef(false);
    // Timer ref for delayed ref reset — keeps the guard active through the entire
    // form machine oscillation window (rules engine may re-assign then re-clear).
    const cascadeResetTimerRef = useRef(null);

    // Ensure we don't leave a dangling timeout when the dropdown unmounts.
    useEffect(() => {
        return () => {
            if (cascadeResetTimerRef.current) {
                clearTimeout(cascadeResetTimerRef.current);
                cascadeResetTimerRef.current = null;
            }
        };
    }, []);

    // Used to pre-clear dependents (Building, Room) in the same React commit as
    // the parent selection — eliminates the 1-frame flash on React Native.
    const filterBoundary = useFilterInterdependencyBoundary();

    // Register so that we can get clearSelection messages when appropriate
    // (i.e. when a parent dropdown selection changes making this
    // selection invalid).
    useRegisterWithFilterInterdependencyBoundary(hNode);
    /**
     * If a clear selection event occurs for this foreignNamespace/foreignRelation, then
     * set the dropdown value to empty.
     * This is probably because a parent dropdown has cleared its selection (i.e. Company > Building)
     */
    useEffect(() => {
        if (foreignNamespace && foreignRelation) {
            return subscribe({ verb: 'clearSelection', namespace: foreignNamespace, relation: foreignRelation }, () => {
                if (hNodeType?.includes('MultiPicker')) {
                    removeAllSelected();
                } else {
                    isCascadeClearingRef.current = true;
                    setValue(emptyValue);
                    setInputValue('');
                }
            });
        }
    }, [subscribe, foreignNamespace, foreignRelation, setValue, setInputValue, emptyValue, removeAllSelected, hNodeType]);

    /**
     * Once form state confirms the cleared value, ensure inputValue is '' and
     * schedule a delayed reset of the cascade guard.  The delay covers the form
     * machine oscillation window where the rules engine may briefly re-assign the
     * building (same name, different company) before the workflow clears it again —
     * without the delay that intermediate assignment would flash on screen.
     */
    const selectedItemIsEmpty = !selectedItem || selectedItem.isDefaultRecord === true;
    useLayoutEffect(() => {
        if (selectedItemIsEmpty) {
            setInputValue('');
            // Delay the ref reset so it stays true through rules-engine oscillations.
            // The ref is cleared immediately in onChange when the user explicitly picks
            // a new value, so this delay only affects automatic/transient assignments.
            if (cascadeResetTimerRef.current) clearTimeout(cascadeResetTimerRef.current);
            cascadeResetTimerRef.current = setTimeout(() => {
                isCascadeClearingRef.current = false;
            }, 800);
        }
    }, [selectedItemIsEmpty, setInputValue]);

    // Handles when a dropdown list entry is selected
    const onChange = useCallback(
        selectedItem => {
            // #2047: the local-only notice is a caption on the list, not an option in it. Unlike
            // the loading/searching/empty rows it appears ALONGSIDE real options, so it is the
            // only unselectable row a user can reach while a valid selection exists — and every
            // path below this point mutates state (placeholders clear the value outright). Leave
            // before any of that runs: clicking a caption must do nothing at all.
            const isLocalOnlyNotice = [termTooShortNotice, serverUnavailableNotice, noServerSearchNotice].includes(
                selectedItem
            );
            if (isLocalOnlyNotice) return;

            // User explicitly picked a value — cascade is definitively over.
            if (cascadeResetTimerRef.current) clearTimeout(cascadeResetTimerRef.current);
            isCascadeClearingRef.current = false;
            // A real user pick of THIS dropdown invalidates its dependent children (Building,
            // Room), so clear them. This is the ONLY place child values are cleared: the
            // FilterInterdependencyBoundary select:success subscription deliberately does NOT
            // clear, so loading a record or navigating between records never wipes children.
            // Clearing here (synchronously, before setValue) also avoids the 1-frame flash on
            // React Native. Placeholder "values" (loading/searching/empty list) are not real
            // user picks, so they must not trigger a clear; an explicit "unassigned" pick does.
            const isPlaceholder = [loadingValue, searchingValue, emptyListValue].includes(selectedItem);
            if (!isPlaceholder && foreignNamespace && foreignRelation) {
                filterBoundary?.clearChildDisplays(foreignNamespace, foreignRelation);
            }
            if (selectedItem === unassignedValue || isPlaceholder) {
                setValue(undefined);
            } else {
                setValue(selectedItem);
                setInputValue(inputItemToString(selectedItem));
            }
        },
        [inputItemToString, setValue, foreignNamespace, foreignRelation, filterBoundary, unassignedValue, loadingValue, searchingValue, emptyListValue, termTooShortNotice, serverUnavailableNotice, noServerSearchNotice]
    );

    // Handles when Downshift tells us that the dropdown is opening or closing
    const onStateChange = useCallback(
        (changes, { inputValue, selectedItem }) => {
            logging.debug('[DROPDOWN] onStateChange changes:', changes);
            _p.onStateChange(
                changes,
                inputValue,
                inputItemToString,
                selectedItem,
                setInputValue,
                native,
                moveFocusToNext,
                active,
                isCascadeClearingRef
            );
        },
        [inputItemToString, moveFocusToNext, active, native]
    );
    // desiredSensorTypes is needed by ReadProvider to capture reads for this
    // component
    const { scanRfid = false, scanBarcode = false } = hNode;
    const desiredSensorTypes = useMemo(() => {
        const _sensorTypes = [constants.sensorTypes.MANUAL];
        if (scanRfid) {
            _sensorTypes.push(constants.sensorTypes.RFID);
        }
        if (scanBarcode) {
            _sensorTypes.push(constants.sensorTypes.BARCODE);
        }
        return _sensorTypes;
    }, [scanRfid, scanBarcode]);

    // Downshift needs a reference to the root component of the dropdown
    // pass this refKey value down into both components so they can coordinate
    // the placement of it and giving it to downshift.
    //const refKey = useRef();
    // prettier-ignore
    return rc(ErrorBoundary, null,
        // Capture desired scan types for this component
        rc(ReadProvider, { id: `${title}${id}`, desiredSensorTypes, hNode, active },
            rc(DownshiftProvider, {
                selectedItem,
                inputItemToString,
                onStateChange,
                inputValue,
                onChange,
                id
            },
                rc(InnerDropDown, {
                    ...props,
                    inputItemToString,
                    listItemToString,
                    setInputValue,
                    unassignedValue,
                    emptyListValue,
                    localOnlyNoticeValues,
                    loadingValue,
                    searchingValue,
                    emptyValue,
                    foreignNamespace,
                    foreignRelation,
                    propertyName,
                    onChange,
                    errors,
                    autoFocus,
                    desiredSensorTypes,
                    selectedValues,
                    setSelectedValues
                })
            )
        )
    );
}
DropDown.displayName = 'AbstractDropDown';
const _DropDown = memo(DropDown);

export default _DropDown;

/**
 * Unless you think of something I didn't, this should only contain code affecting the props
 * provided by Downshift (e.g. input props) and the actual component rendering.
 * @typedef {Object} Props
 * @property {Object} hNode
 * @property {string} currentRoute
 */
/** @type {import('react').FC<Props>} */
const InnerDropDown = props => {
    const theme = useTheme();
    const { mobile, native, viewMargin, selectedTheme } = theme;
    const {
        // prop getters
        getRootProps,
        getMenuProps,
        getItemProps,
        getInputProps,
        getToggleButtonProps,
        getLabelProps,
        // actions
        closeMenu,
        openMenu,
        setHighlightedIndex,
        // state
        highlightedIndex,
        isOpen,
        inputValue,
        selectItem,
        selectedItem,
        addInputChangeHandler
    } = useDownshift();

    // Some consumers of this abstract component use namespace/relation instead of
    // foreignNamespace/foreignRelation.
    const foreignNamespace = props.hNode.foreignNamespace || props.hNode.namespace;
    const foreignRelation = props.hNode.foreignRelation || props.hNode.relation;
    const {
        // From Dropdown (above)
        inputItemToString,
        listItemToString,
        setInputValue,
        unassignedValue,
        emptyListValue,
        localOnlyNoticeValues,
        loadingValue,
        searchingValue,
        emptyValue,
        selectedValues,
        setSelectedValues,
        isCascadeClearingRef,
        // from original props
        value = emptyValue,
        disabled: _disabled,
        className,
        style,
        hNode,
        propertyName,
        hNode: {
            id,
            displayUnassignedRow = true,
            alwaysDisplayUnassignedRow = false,
            autoHide: _autoHide = false,
            treePosition: { sequence },
            hNodeType
        },
        records: fixedRecords,
        errors,
        autoFocus,
        onFocus: _onFocus,
        onBlur: _onBlur,
        supportWildCard = false,
        // Optional. Called once the auto-hide check has come back positive and this control
        // is about to remove itself. Only the search pane passes it -- a hidden search
        // control must not leave a criterion in the query (#1977).
        onAutoHide
    } = props || {};
    const autoHide = asBoolean(_autoHide);
    const rootProps = getRootProps();
    const rootRef = useInterceptRef(rootProps);
    const menuProps = getMenuProps({}, { suppressRefError: true });
    const menuRef = useInterceptRef(menuProps);
    const [inputIsDirty, setInputIsDirty] = useState(false);
    const [autoHideTriggered, setAutoHideTriggered] = useState(false);
    const delayedSetInputIsDirty = useTimeout(setInputIsDirty);
    const { mostRecentIndividualRead, reads, reset: resetReads } = _p.useReads();
    const moveFocusToNext = useMoveFocusToNext();
    const allowStateChange = useRef(true);
    const disabledByFilter = _p.useIsDisabledByFilter(hNode);
    const disabled = _disabled || disabledByFilter;

    // publish is not used here, leaving it in for clarity
    // eslint-disable-next-line no-unused-vars
    const [subscribe, publish, request] = useEventSink();
    const [newReads, setNewReads] = useState(null);

    // If a user event occurs that implies the user would prefer to select an item
    // that matches the dropdown input (e.g. barcode scan or enter button typed),
    // then set this to true. This will cause a useEffect to select the item if
    // there is only one item that matches the input.
    const [selectItemIfPossible, setSelectItemIfPossible] = useState(false);

    // Mounts the dropdown list for non-mobile UIs (uses Popper.js)
    const inputAndButtonRef = useRef();
    useEffect(() => {
        let instance;
        if (isOpen && !mobile) {
            // Because of the !mobile flag, this should not be hit in react-native.
            // Downshift uses refs to keep track of the pieces of the dropdown and that
            // makes forwarding refs really complicated, so this querySelector
            // simplifies things.
            const menu = document.querySelector(`#bb${id}-menu`);
            instance = _p.createPopper(inputAndButtonRef.current, menu, {
                strategy: 'fixed',
                placement: 'bottom-start',
                modifiers: [
                    { name: 'flip', enabled: true },
                    { name: 'hide', enabled: true },
                    {
                        name: 'offset',
                        options: {
                            offset: [
                                selectedTheme === 'gl2025' ? theme.formField.padding.left : viewMargin,
                                -1 * viewMargin
                            ]
                        }
                    }
                ]
            });
        }
        return () => {
            // destroy the popper instance when the dropdown closes.
            if (instance != null) instance.destroy();
        };
    }, [isOpen, mobile, id, viewMargin, selectedTheme, theme.formField?.padding?.left]);

    // set list width based on width of input element
    const [listWidth, setListWidth] = useState(0);
    useLayoutEffect(() => {
        if (__SST_REACT_NATIVE__) return;

        const inputElement = inputAndButtonRef.current;
        if (!inputElement) return;

        const observer = new ResizeObserver(entries => {
            for (let entry of entries) {
                if (entry.borderBoxSize) {
                    setListWidth(entry.borderBoxSize[0].inlineSize);
                } else {
                    setListWidth(entry.contentRect.width);
                }
            }
        });

        observer.observe(inputElement);

        return () => observer.disconnect();
        // need inputAndButtonRef.current as a dep for it to properly observe resize in some cases
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputAndButtonRef.current]);

    // Prevent async ops from setting state if the component is unmounted
    useEffect(
        () => () => {
            allowStateChange.current = false;
        },
        []
    );

    const onFocus = useCallback(
        e => {
            _onFocus?.(e);
        },
        [_onFocus]
    );

    // These ref temp values are to prevent the onBlur from being recreated when these
    // values change, but still give access to the latest values inside the onBlur callback
    // (via a the useRef.current reference).
    // This is necessary because recreating onBlur sets off a chain of events:
    // onModalChange is recreated which in turn causes the modal.native.js
    // to call onChange when nothing has really changed.
    const tempValue = useRef();
    tempValue.current = value;
    const tempInputIsDirty = useRef();
    tempInputIsDirty.current = inputIsDirty;
    const onBlur = useCallback(
        e => {
            if (native) {
                resetReads();
                _onBlur?.(e);
                return;
            }
            // Get the menu element ref (even if menuRef is function ref)
            let menuElement;
            if (typeof menuRef === 'function') {
                menuElement = menuRef();
            } else {
                menuElement = menuRef.current;
            }
            // If the new relatedTarget is not the dropdown menu or inside the rootRef then blur
            if (
                e.relatedTarget == null ||
                (!rootRef.current.contains(e.relatedTarget) && menuElement !== e.relatedTarget)
            ) {
                // When focus leaves the control, if the input value is dirty, revert it to the
                // selected item's text — but not during a cascade clear (dirty is a false
                // positive from setInputValue('') running before selectedItem catches up,
                // which would restore the old building name on web).
                if (tempInputIsDirty.current && !isCascadeClearingRef?.current) {
                    setInputValue(tempValue.current?.title ?? '');
                }
                //reset the Read Context. If a selection happened due to it filtering down to 1,
                //that selection is maintained elsewhere
                resetReads();
                _onBlur?.(e);
            }
        },
        [_onBlur, rootRef, menuRef, setInputValue, resetReads, native]
    );

    const searchText = inputItemToString(selectedItem) === inputValue ? '' : inputValue;
    let {
        recordCount,
        predefinedRecordCount,
        records: _records,
        viewReady,
        isSearching,
        localOnlySearchReason
    } = _p.useAssembleRecords(
        hNode,
        searchText,
        fixedRecords,
        // todo: we had to basically hardcode this to true when hNodeType is MultiPickerDropDown.
        //  We saw a very occasional issue where the dropdown appears frozen until you close it and reopen,
        //  which may be due to this change. It probably shouldn't always be true.
        isOpen || autoHide || reads?.get()?.length || hNodeType === 'MultiPickerDropDown',
        supportWildCard
    );

    // Avoid several react warnings by putting this outside the render cycle
    // inside a timeout.
    const selectRecordIfPossible = useTimeout(
        selectedItem => {
            setInputValue(inputItemToString(selectedItem));
            selectItem(selectedItem);
            closeMenu();
            //we got what we need. clear the reads to avoid subsequent filtering
            resetReads();
            // Move focus to the next element in the UI
            moveFocusToNext();
        },
        [setInputValue, selectItem, closeMenu, resetReads, inputItemToString, moveFocusToNext],
        0
    );

    const [records, setRecords] = useState(EMPTY_ARRAY);
    const [recordsReady, setRecordsReady] = useState(viewReady);

    // For MultiPickerDropDown, if the available records change, filter out the selected values that are no longer valid
    useEffect(() => {
        if (hNodeType !== 'MultiPickerDropDown') {
            return;
        }
        // don't do this filtering until the records are ready, or we will be filtering based on invalid data
        if (!recordsReady) {
            return;
        }
        const recordIds = records.map(record => record._id);
        const newSelectedValues = selectedValues.filter(x => recordIds.includes(x._id));
        setSelectedValues(newSelectedValues);
    }, [disabled, hNodeType, records, selectedValues, setSelectedValues, recordsReady]);

    // For RFID We might get a bunch of sensor read changes, each adding new values to the
    // previous.  This makes sense if you are rendering something a user might see -like a grid
    // but not if you are doing a bunch of filtering each time a read comes in as in this
    // dropdown.  This will wait a second after the last read before processing the reads.
    const setNewReadsDebounced = useDebounce(
        payload => {
            if (payload == null || !Array.isArray(payload) || payload.length === 0) return;
            setNewReads(payload); // this will trigger filter useEffect below,
        },
        [],
        READS_RECEIVED_DEBOUNCE
    );

    // subscribe to change in sensor reads and trigger filter useEffect below
    useEffect(() => {
        // handle the subscription to the sensor reads using the above debounced function
        return subscribe({ verb: 'change', namespace: 'sensor', relation: 'read' }, setNewReadsDebounced);
    }, [subscribe, setNewReadsDebounced]);

    // If there are new reads, filter the records based on the reads.
    useEffect(() => {
        //use a post-filter, rather than trying to somehow merge this into useAssembleRecords
        if (viewReady) {
            const _reads = reads.get();
            if (_reads.length > 0) {
                if (_reads[0].sensorType === 'BARCODE') {
                    // If the first is a barcode read and there is more than one, then throw an error
                    if (_reads.length > 1) {
                        logging.error(
                            `[DROPDOWN] Multiple barcode reads (${_reads.length}) received, but only one is expected.`
                        );
                    }
                    const filteredRecords = _records.filter(record =>
                        _reads.some(r => [record[propertyName]].includes(r.asciiTagId))
                    );
                    logging.debug(
                        `[DROPDOWN] Filtered ${_records.length} records using ${_reads.length} reads, with ${filteredRecords.length} results.`
                    );
                    // If we have matches
                    if (filteredRecords.length > 0) {
                        //if there is only 1 record that matches
                        if (filteredRecords.length === 1) {
                            //select that record
                            const selectedItem = filteredRecords[0];
                            selectRecordIfPossible(selectedItem);
                            setRecords(filteredRecords);
                        } else {
                            //if multiple records matched, return all matching records
                            openMenu();
                            setRecords(filteredRecords);
                        }
                        return;
                    }
                } else {
                    // get the record that matches a read and has the highest rssi property value.
                    let matchingRecordCount = 0;
                    const highestRssiRead = _records.reduce((highest, record) => {
                        const matchingRead = _reads.find(r => {
                            if (r.sensorType !== 'RFID') {
                                logging.warn(
                                    `[DROPDOWN] Unexpected sensor type ${r.sensorType} in reads, expected RFID.`
                                );
                                // Maybe the user tried to scan a barcode at the same time?  ignore it.
                                return false;
                            }
                            return [record.tagId, record.locationTagId].includes(r.tagId);
                        });
                        if (matchingRead != null) {
                            matchingRecordCount++;
                            const current = { record, rssi: matchingRead.rssi };
                            if (current.rssi > (highest?.rssi ?? -Infinity)) {
                                return current;
                            }
                        }
                        return highest;
                    }, undefined);
                    if (highestRssiRead != null) {
                        logging.debug(
                            `[DROPDOWN] of ${
                                _records.length
                            } records, ${matchingRecordCount} match RFID reads.  Highest rssi read is: ${
                                highestRssiRead.record?.tagId ?? highestRssiRead.record?.locationTagId
                            } with rssi ${highestRssiRead?.rssi}.`
                        );
                        selectRecordIfPossible(highestRssiRead.record);
                        setRecords([highestRssiRead.record]);
                        return;
                    }
                    logging.debug(`[DROPDOWN] of ${_records.length} records, 0 match RFID reads.`);
                }
            }
        }
        //if nothing matched, or if there were no reads to begin with, return the original record set.
        setRecords(_records);

        // if viewReady is true, by this point we've set `records` to the correct value, so set recordsReady to true
        if (viewReady) {
            setRecordsReady(true);
        }
    }, [selectRecordIfPossible, viewReady, reads, _records, openMenu, propertyName, newReads]);

    // #2047: these options are only the ones stored on this device, so say so rather than letting
    // a partial list read as the whole one.
    const localOnlyNoticeValue = localOnlySearchReason ? localOnlyNoticeValues?.[localOnlySearchReason] : undefined;

    // An empty list is where a partial list misleads most - "no such record" and "no such record
    // here" look identical - so the notice replaces the generic empty hint rather than adding to it.
    const valueToDisplayWhenListEmpty = !viewReady
        ? loadingValue
        : isSearching
          ? searchingValue
          : (localOnlyNoticeValue ?? emptyListValue);

    // This is to prevent the useEffect cleanup (and addInputChangeHandler unsubscribe) below
    // from running when mostRecentIndividualRead changes.
    // Reads were getting lost in between the time when the change handler was unsubscribed
    // and resubscribed.
    const tempMostRecentIndividualRead = useRef();
    tempMostRecentIndividualRead.current = mostRecentIndividualRead;
    // This is included here because it changes isDirty and isDirty is in here because
    // it is only needed by onBlur and the DropDownList.
    // The content of this hook should probably only be called when mounting the dropdown,
    // but addInputChangeHandler returns an unsubscribe function just in case
    useEffect(() => {
        // Listen for input value changes
        const removeHandler = addInputChangeHandler((inputValue, { selectedItem }) => {
            logging.debug(`[INNERDROPDOWN] Input value changed to ${inputValue}`);
            // During a cascade clear the form record oscillates (e.g. building goes
            // {} → {_id,title} → undefined). Each transition fires componentDidUpdate
            // which calls setInputValue(oldBuildingName) and causes a visible flash.
            // Block any non-empty value from being set while cascade is in flight.
            if (isCascadeClearingRef?.current && inputValue !== '') {
                return;
            }
            setInputValue(inputValue);
            // If the input has something in it other than the selectedItem title,
            // then set inputIsDirty to true;
            const isDirty = inputItemToString(selectedItem) !== inputValue;
            if (!isDirty) {
                // Wait a second for view data to catch up (i.e. repopulate with unfiltered data)
                // or the value will be cleared.
                // See "Value Validation" above for code that clears it.
                delayedSetInputIsDirty(false);
            } else {
                setInputIsDirty(true);
            }
        });
        return () => {
            logging.debug('[INNERDROPDOWN] removeHandler called');
            removeHandler();
        };
    }, [addInputChangeHandler, delayedSetInputIsDirty, inputItemToString, setInputValue]);

    // If a user event occurs that implies the user would prefer to select an item
    // that matches the dropdown input (e.g. barcode scan or enter button typed),this
    // will select the item if there is only one item that matches the input.
    useEffect(() => {
        if (
            (selectItemIfPossible || mostRecentIndividualRead?.tagId === inputValue) &&
            records.length === 1 &&
            inputIsDirty
        ) {
            selectItem(records[0]);
            closeMenu();
            resetReads();
        } else {
            if (selectItemIfPossible) {
                // set this back to false until Enter is hit again so it doesn't trigger mid-typing
                setSelectItemIfPossible(false);
            }
        }
    }, [
        selectItemIfPossible,
        records,
        mostRecentIndividualRead,
        selectItem,
        closeMenu,
        resetReads,
        inputIsDirty,
        inputValue
    ]);

    useEffect(() => {
        if (autoHide && viewReady) {
            const onlyOneRecord = predefinedRecordCount != null
                ? Promise.resolve(predefinedRecordCount === 1)
                : _p.hasOnlyOneRecord(foreignNamespace, foreignRelation, request);
            onlyOneRecord.then(onlyOne => {
                if (onlyOne && records.length === 1) {
                    setAutoHideTriggered(true);
                    // Tell the consumer before the selection propagates, so it can decide
                    // what a selection made by a control the user cannot see is allowed to
                    // do. Called on both branches below, because the control hides either way.
                    onAutoHide?.();
                    if (!(selectedItem && selectedItem._id === records[0]?._id)) {
                        //#5992 if the currently selected item is already the same as our selection,
                        //don't re select the same record which will trigger a clearSelection event
                        //and will clear dependent drop downs.
                        selectItem(records[0]);
                    }
                }
            });
        }
    }, [records, autoHide, viewReady, predefinedRecordCount, selectItem, selectedItem, request, foreignNamespace, foreignRelation, onAutoHide]);

    const inputProps = getInputProps();
    inputProps.inputRef = useRef();
    inputProps.name = `drop-down-input-${hNode.title}`;
    inputProps.onBlur = onBlur;
    // If the modal unmounts, also call onBlur
    const onModalChange = useCallback(
        changeType => {
            if (changeType === 'unmount') {
                onBlur({});
            }
        },
        [onBlur]
    );

    inputProps.onFocus = onFocus;
    inputProps.onKeyDown = useCallback(
        e => {
            if (e.key === 'Enter') {
                setSelectItemIfPossible(true);
                // Don't close the dropdown on enter.
                // If subsequent selection of item is successful, dropdown will close anyway.
                // This also prevents Enter on a closed dropdown from triggering
                // implicit form submission or other unintended behavior.
                e.preventDefault();
            } else if (e.key === 'Unidentified') { // barcode scan key
                setSelectItemIfPossible(true);
            }
        },
        []
    );

    useEffect(() => {
        if (isOpen) {
            inputProps.inputRef.current?.focus();
        }
    }, [inputProps.inputRef, isOpen]);

    // To avoid database lookups, extra stuff to render, etc. only render list if dropdown is open
    const RenderedDropDownList = !isOpen
        ? null
        : rc(DropDownList, {
              displayUnassignedRow,
              alwaysDisplayUnassignedRow,
              getItemProps,
              menuProps,
              highlightedIndex,
              hNode,
              id,
              inputIsDirty,
              isOpen,
              isSearching,
              listItemToString,
              records,
              recordCount,
              unassignedValue,
              loadingValue,
              emptyListValue: valueToDisplayWhenListEmpty,
              localOnlyNoticeValue,
              value,
              closeMenu,
              setHighlightedIndex,
              onFocus,
              onBlur,
              selectedValues,
              width: listWidth
          });

    const hasErrors = Array.isArray(errors) ? errors.length > 0 : Boolean(errors);

    // If autoHide is enabled and there is only one record in the starting
    // recordset, the hide the dropdown.
    if (autoHideTriggered || (!viewReady && autoHide)) return null;

    // Separate the main portion of the dropdown.  This is to allow it to be
    // rendered in a modal for native devices, and without for desktops.
    // prettier-ignore
    const mainPortion = rc(ComboContainer, { id: `comboContainer-${id}`, isOpen, hasErrors, ...rootProps, 'aria-labelledby': undefined },
        rc(ScanLabelAndField, null,
            rc(InputAndButton, { ref: inputAndButtonRef, isOpen },
                rc(DropDownInput, {
                    disabled,
                    autoFocus: autoFocus || (mobile && isOpen),
                    inputProps,
                    labelProps: getLabelProps(),
                    hNode,
                    className,
                    style,
                    errors,
                    sequence,
                    isOpen,
                    toggleButtonProps: getToggleButtonProps()
                }),
                selectedTheme !== 'gl2025' && !disabled && rc(ComboButton, {
                    isOpen,
                    mobile,
                    id,
                    onBlur,
                    toggleButtonProps: getToggleButtonProps()
                })
            )
        ),
        RenderedDropDownList
    );

    // Native doesn't **really** do absolute position, clicks are lost and sometimes elements
    // don't render if the control moves outside the rectangle of its parent, so we must put
    // it in a Modal to have it go full screen.
    if (native && isOpen) {
        return rc(
            Modal,
            {
                id: `dropdownModal-${id}`,
                onChange: onModalChange,
                visible: true,
                ...testProperties(hNode, 'modal')
            },
            mainPortion
        );
    }
    return mainPortion;
};
InnerDropDown.displayName = 'InnerDropDown';
InnerDropDown.propTypes = {
    hNode: PropTypes.shape({
        foreignNamespace: PropTypes.string,
        foreignRelation: PropTypes.string,
        namespace: PropTypes.string,
        relation: PropTypes.string,
        treePosition: PropTypes.shape({
            sequence: PropTypes.number.isRequired
        }).isRequired
    }).isRequired,
    active: PropTypes.bool.isRequired
};

/**
 * This has been extracted to allow it to be tested independently.  It has
 * a lot of arguments because many of these are inside the components scope.
 * @param {object} changes - change object supplied by downshift
 * @param {string} inputValue - current input element value
 * @param {function} inputItemToString - converts an item to the string to display in the input
 * @param {object} selectedItem - the selected item
 * @param {function} setInputValue
 * @param {bool} native - true if running on a native device
 * @param {function} moveFocusToNext - moves the UI focus to the next eligible component
 * @param {bool} active - true if the dropdown has focus
 */
function onStateChange(
    changes,
    inputValue,
    inputItemToString,
    selectedItem,
    setInputValue,
    native,
    moveFocusToNext,
    active,
    isCascadeClearingRef
) {
    if (changes.hasOwnProperty('isOpen')) {
        // Drop down closes
        if (!changes.isOpen) {
            // Block the revert if a cascade clear is in flight — selectedItem is still
            // the stale old value while the form state machine catches up, so reverting
            // would restore the old building name and restart the DB filter loop.
            if (!changes.isOpen && inputValue !== inputItemToString(selectedItem) && !isCascadeClearingRef?.current) {
                setInputValue(inputItemToString(selectedItem));
            }
            // These 👇could be combined to one `if` statement, but this seems a little easier to mentally
            // parse
            // Native downshift reports an '__autocomplete_unknown__' change type when the user
            // types an Enter key to select a value.
            if (native && ['__autocomplete_unknown__', 0].includes(changes.type)) {
                /* Production downshift uses numbers for these constants 😒 */
                moveFocusToNext();
            } else if (active) {
                // web and input active and user types Enter key.
                moveFocusToNext();
            } else if (
                // User clicks the item in the dropdown menu
                // Production downshift uses numbers for these constants 😒
                ['__autocomplete_click_item__', 9].includes(changes.type) &&
                changes.selectedItem &&
                !changes.selectedItem.isDefaultRecord
            ) {
                moveFocusToNext();
            }
        }
    }
}

/**
 * Extracts the display string from the entry, preferring to extract using the
 * displayProperties, but falling back on the propertyName.
 * @param {Object} entry
 * @param {string} [propertyName] - If there are no displayProperties or there isn't any data on the entry for the displayProperties, this will be used instead.  If there is a title alternative (e.g. assetNo or rmaNo) it should already have been determined and passed in.  This method won't do it for you.
 * @param {string[]} [displayProperties=[]]
 * @returns {string} string
 */
function getItemValuesText(entry, propertyName, displayProperties = []) {
    if (!displayProperties.length) {
        return get(entry, propertyName, '');
    }

    const displayValues = displayProperties.map(dp => get(entry, dp, ''));
    const existingDisplayValues = displayValues.filter(dv => !!dv);

    // This may be legacy data, ensure something displays.
    if (existingDisplayValues.length === 0) {
        return get(entry, propertyName, '');
    }

    return existingDisplayValues.join(', ');
}

function asBoolean(value) {
    if (typeof value === 'boolean') return value;
    else if (typeof value === 'string') return value.toLowerCase() === 'true';
    return !!value;
}

/**
 * This should get an entry containing the minimalText in all fields needed
 * to display the entry properly (in both the input and menu item displays).
 * For use with things like the 'Unassigned' entry or the '' (empty text)
 * entry.
 * @param {object} hNode
 * @param {string} minimalText - the text the user should see when viewing this item
 * @returns an entry to display in the dropdown
 */
function useMinimalEntry(hNode, minimalText, use0Id = false) {
    return useMemo(() => {
        const foreignNamespace = hNode?.foreignNamespace || hNode?.namespace;
        const foreignRelation = hNode?.foreignRelation || hNode?.relation;
        // use || here instead of ?? because propertyName might be === '' when `inputDisplayProperties` and `dropdownDisplayProperties` are configured.
        const propertyName = hNode?.propertyName || getTitleAlternative(foreignNamespace, foreignRelation);
        const inputDisplayProperties = hNode?.inputDisplayProperties ?? emptyArray;
        const dropdownDisplayProperties = hNode?.dropdownDisplayProperties ?? emptyArray;
        let minimalFields = [];
        if (inputDisplayProperties.length > 0) {
            minimalFields.push(inputDisplayProperties[0]);
        }
        if (dropdownDisplayProperties.length > 0) {
            minimalFields.push(dropdownDisplayProperties[0]);
        }
        if (minimalFields.length === 0) {
            minimalFields.push(propertyName);
        }
        minimalFields = uniq(minimalFields);
        const _id = use0Id ? '0'.repeat(24) : undefined;
        const entry = { isDefaultRecord: true, _id };
        minimalFields.forEach(field => {
            set(entry, field, minimalText);
        });
        return entry;
    }, [hNode, minimalText, use0Id]);
}

/**
 * A row saying the list is only what is stored on this device (#2047). A default entry like the
 * others, but with an `_id` of its own: unlike them it is shown alongside Unassigned and real
 * options, and the list marks a row as already chosen by matching `_id` against the chosen values,
 * so sharing Unassigned's `_id` would draw the notice as chosen whenever Unassigned is.
 * @param {object} hNode
 * @param {string} reason - a value from LOCAL_ONLY_SEARCH_REASONS
 */
function useLocalOnlyNotice(hNode, reason) {
    const entry = useMinimalEntry(hNode, LOCAL_ONLY_NOTICE_TEXT[reason]);
    return useMemo(() => ({ ...entry, _id: `__local_only_notice_${reason}__` }), [entry, reason]);
}

async function hasOnlyOneRecord(namespace, relation, request) {
    const result = await request(
        { criteria: { 'meta.deleted': { $exists: false } } },
        {
            verb: 'count',
            namespace,
            relation
        }
    );
    return result?.result?.[0] === 1;
}
